// Yeast Garden — 3×3 plot grid. Plant species with crumbs, harvest for buffs
// or permanent rate boosts. Plot grow timers tick on real elapsed time so a
// backgrounded tab still matures plants.
import { GARDEN_PLOT_COUNT, GARDEN_GRID_SIZE, GARDEN_BARLEY_CAP, GARDEN_POLLINATE_CHANCE, GARDEN_WATER_COST, SPECIES } from '../content/species.js';
import { state, save } from '../systems/state.js';
import { hasActiveBuff, gardenBonus } from '../systems/rate.js';
import { toast } from './toast.js';

const gardenGridEl = document.getElementById('garden-grid');
const gardenBonusEl = document.getElementById('garden-bonus');
const gardenSummaryEl = document.getElementById('garden-summary');
const waterBtnEl = document.getElementById('water-btn');
let speciesPickerEl = null;
let onRefreshMenu = null;
let onSpawnGolden = null;

export function initGarden({ onRefreshMenuCb, onSpawnGoldenCb }) {
  onRefreshMenu = onRefreshMenuCb;
  onSpawnGolden = onSpawnGoldenCb;
  if (waterBtnEl) waterBtnEl.addEventListener('click', waterRandomPlot);
}

export function plotGrowProgress(plot) {
  const sp = SPECIES.find(s => s.id === plot.species);
  if (!sp) return 1;
  // Stardust pauses while no buff is active. Use lastTickAt accumulator.
  const accumulated = plot.lastTickAt - plot.plantedAt;
  return Math.max(0, Math.min(1, accumulated / sp.growMs));
}

export function plotIsMature(plot) { return plot.species && plotGrowProgress(plot) >= 1; }

function emptyNeighbors(idx) {
  const x = idx % GARDEN_GRID_SIZE, y = Math.floor(idx / GARDEN_GRID_SIZE);
  const dirs = [[1,0],[-1,0],[0,1],[0,-1]];
  let count = 0;
  for (const [dx, dy] of dirs) {
    const nx = x + dx, ny = y + dy;
    if (nx < 0 || ny < 0 || nx >= GARDEN_GRID_SIZE || ny >= GARDEN_GRID_SIZE) continue;
    if (!state.garden[ny * GARDEN_GRID_SIZE + nx].species) count++;
  }
  return count;
}

function neighborsOfSpecies(idx, species) {
  const x = idx % GARDEN_GRID_SIZE, y = Math.floor(idx / GARDEN_GRID_SIZE);
  const dirs = [[1,0],[-1,0],[0,1],[0,-1]];
  let n = 0;
  for (const [dx, dy] of dirs) {
    const nx = x + dx, ny = y + dy;
    if (nx < 0 || ny < 0 || nx >= GARDEN_GRID_SIZE || ny >= GARDEN_GRID_SIZE) continue;
    if (state.garden[ny * GARDEN_GRID_SIZE + nx].species === species) n++;
  }
  return n;
}

function canPlant(species, idx) {
  if ((state.crumbs || 0) < species.cost) return { ok: false, reason: `costs ${species.cost} crumb${species.cost === 1 ? '' : 's'}` };
  if (species.needsEmptyNeighbors && emptyNeighbors(idx) < species.needsEmptyNeighbors) {
    return { ok: false, reason: `needs ${species.needsEmptyNeighbors} empty neighbors` };
  }
  if (species.requiresAdjacent && neighborsOfSpecies(idx, species.requiresAdjacent) < 1) {
    return { ok: false, reason: `must be adjacent to ${species.requiresAdjacent}` };
  }
  return { ok: true };
}

function plantInPlot(idx, speciesId) {
  const species = SPECIES.find(s => s.id === speciesId);
  if (!species) return;
  const plot = state.garden[idx];
  if (plot.species) return;
  const check = canPlant(species, idx);
  if (!check.ok) { toast(`can't plant: ${check.reason}`); return; }
  state.crumbs -= species.cost;
  plot.species = species.id;
  plot.plantedAt = Date.now();
  plot.lastTickAt = Date.now();
  // Void mold destroys cardinal neighbors on plant.
  if (species.destroysNeighbors) {
    const x = idx % GARDEN_GRID_SIZE, y = Math.floor(idx / GARDEN_GRID_SIZE);
    let refunds = 0;
    for (const [dx, dy] of [[1,0],[-1,0],[0,1],[0,-1]]) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= GARDEN_GRID_SIZE || ny >= GARDEN_GRID_SIZE) continue;
      const n = state.garden[ny * GARDEN_GRID_SIZE + nx];
      if (n.species) {
        const ns = SPECIES.find(s => s.id === n.species);
        if (ns) refunds += Math.floor(ns.cost * 0.5);
        n.species = null; n.plantedAt = 0; n.lastTickAt = 0;
      }
    }
    if (refunds > 0) { state.crumbs += refunds; toast(`mold destroyed neighbors. +${refunds} crumbs.`); }
  }
  save(); renderGarden(); onRefreshMenu();
}

function harvestPlot(idx) {
  const plot = state.garden[idx];
  if (!plotIsMature(plot)) return;
  const species = SPECIES.find(s => s.id === plot.species);
  if (!species) return;
  // Apply harvest reward.
  if (species.buffMult && species.buffMs) {
    state.activeEffects.push({ id: 'g_' + species.id, expiresAt: Date.now() + species.buffMs, mult: species.buffMult });
  }
  if (species.chaosPaceMs) {
    state.activeEffects.push({ id: 'g_hops', expiresAt: Date.now() + species.chaosPaceMs, mult: 1 });
  }
  if (species.permanentBonus) {
    state.barleyBonus = Math.min(GARDEN_BARLEY_CAP, (state.barleyBonus || 0) + species.permanentBonus);
  }
  if (species.spawnsGolden) {
    onSpawnGolden();
  }
  if (species.crownReward) {
    state.crowns = (state.crowns || 0) + species.crownReward;
  }
  toast(`🌱 harvested ${species.name}`);
  // Cross-pollination: 5% chance per matching neighbor to seed an empty plot
  // with a random non-mold species.
  const matches = neighborsOfSpecies(idx, species.id);
  if (matches > 0 && Math.random() < GARDEN_POLLINATE_CHANCE * matches) {
    const empty = [];
    for (let i = 0; i < GARDEN_PLOT_COUNT; i++) {
      if (!state.garden[i].species) empty.push(i);
    }
    if (empty.length) {
      const target = empty[Math.floor(Math.random() * empty.length)];
      // Filter to species whose placement rules are satisfied at the target.
      const candidates = SPECIES.filter(s =>
        s.id !== 'voidmold' &&
        (!s.requiresAdjacent || neighborsOfSpecies(target, s.requiresAdjacent) > 0) &&
        (!s.needsEmptyNeighbors || emptyNeighbors(target) >= s.needsEmptyNeighbors) &&
        (!s.requiresBuff || hasActiveBuff())
      );
      if (candidates.length) {
        const hybrid = candidates[Math.floor(Math.random() * candidates.length)];
        state.garden[target].species = hybrid.id;
        state.garden[target].plantedAt = Date.now();
        state.garden[target].lastTickAt = Date.now();
        toast(`🌱 a wild ${hybrid.name} sprouted next door.`);
      }
    }
  }
  // Clear plot.
  plot.species = null; plot.plantedAt = 0; plot.lastTickAt = 0;
  save(); renderGarden(); onRefreshMenu();
}

function clearPlot(idx) {
  const plot = state.garden[idx];
  if (!plot.species) return;
  // Caller is responsible for the confirm dialog (avoids double-prompt).
  plot.species = null; plot.plantedAt = 0; plot.lastTickAt = 0;
  save(); renderGarden();
}

function waterRandomPlot() {
  if ((state.count || 0) < GARDEN_WATER_COST) { toast(`watering can needs ${GARDEN_WATER_COST} pumpernickels`); return; }
  // Pick the plot closest to ready (most progressed but not yet mature).
  let target = -1, best = -1;
  for (let i = 0; i < GARDEN_PLOT_COUNT; i++) {
    const p = state.garden[i];
    if (!p.species || plotIsMature(p)) continue;
    const prog = plotGrowProgress(p);
    if (prog > best) { best = prog; target = i; }
  }
  if (target < 0) { toast('nothing growing yet.'); return; }
  state.count -= GARDEN_WATER_COST;
  const plot = state.garden[target];
  const species = SPECIES.find(s => s.id === plot.species);
  // Halve remaining grow time by advancing lastTickAt.
  const remaining = species.growMs - (plot.lastTickAt - plot.plantedAt);
  plot.lastTickAt += remaining / 2;
  toast(`💧 watered the ${species.name}`);
  save(); renderGarden();
}

function openSpeciesPicker(idx) {
  if (speciesPickerEl && speciesPickerEl.parentNode) return; // already open, ignore re-clicks
  const ovEl = document.createElement('div');
  speciesPickerEl = ovEl;
  ovEl.className = 'ach-modal show';
  ovEl.style.zIndex = '500';
  const card = document.createElement('div');
  card.className = 'ach-card';
  card.innerHTML = `
    <button class="ach-close" aria-label="Close">×</button>
    <div class="ach-title">🌱 plant in plot ${idx + 1}</div>
    <div class="ach-sub">crumbs: ${state.crumbs || 0}</div>
    <div class="species-picker"></div>
  `;
  ovEl.appendChild(card);
  document.body.appendChild(ovEl);
  const grid = card.querySelector('.species-picker');
  for (const sp of SPECIES) {
    const check = canPlant(sp, idx);
    const row = document.createElement('div');
    row.className = 'species-row' + (check.ok ? '' : ' locked');
    row.innerHTML = `
      <div class="species-icon">${sp.icon}</div>
      <div class="species-info">
        <div class="species-name">${sp.name}</div>
        <div class="species-desc">${sp.desc}${check.ok ? '' : ' — ' + check.reason}</div>
      </div>
      <div class="species-cost">${sp.cost === 0 ? 'free' : sp.cost + '🍞'}</div>
    `;
    if (check.ok) row.addEventListener('click', () => { plantInPlot(idx, sp.id); document.body.removeChild(ovEl); });
    grid.appendChild(row);
  }
  const close = () => { if (ovEl.parentNode) document.body.removeChild(ovEl); speciesPickerEl = null; };
  card.querySelector('.ach-close').addEventListener('click', close);
  ovEl.addEventListener('click', (e) => { if (e.target === ovEl) close(); });
}

export function closeSpeciesPicker() {
  if (speciesPickerEl && speciesPickerEl.parentNode) {
    document.body.removeChild(speciesPickerEl);
    speciesPickerEl = null;
  }
}

export function renderGarden() {
  if (!gardenGridEl) return;
  gardenGridEl.innerHTML = '';
  for (let i = 0; i < GARDEN_PLOT_COUNT; i++) {
    const plot = state.garden[i];
    const cell = document.createElement('div');
    cell.className = 'plot';
    if (!plot.species) {
      cell.classList.add('empty');
      cell.innerHTML = `<div class="plot-icon">＋</div><div class="plot-time">empty</div>`;
      cell.addEventListener('click', () => openSpeciesPicker(i));
    } else {
      const sp = SPECIES.find(s => s.id === plot.species);
      const mature = plotIsMature(plot);
      const progress = plotGrowProgress(plot);
      cell.classList.add(mature ? 'mature' : 'planted');
      const remaining = mature ? 0 : Math.max(0, sp.growMs - (plot.lastTickAt - plot.plantedAt));
      const minutes = Math.floor(remaining / 60000), seconds = Math.floor((remaining % 60000) / 1000);
      const paused = sp.requiresBuff && !state.activeEffects.some(e => Date.now() < e.expiresAt && e.mult > 1);
      const timeText = mature ? 'harvest!' : (paused ? 'paused (no buff)' : `${minutes}:${seconds.toString().padStart(2, '0')}`);
      cell.innerHTML = `
        <div class="plot-icon">${sp.icon}</div>
        <div class="plot-time ${mature ? 'ready' : (paused ? 'paused' : '')}">${timeText}</div>
        <div class="plot-bar"><i style="width:${(progress * 100).toFixed(1)}%"></i></div>
      `;
      if (mature) {
        cell.addEventListener('click', () => harvestPlot(i));
      } else {
        // Tap on a non-mature plot shows status; long-press (500ms) uproots.
        // Avoids the destructive native confirm dialog on every accidental tap.
        let pressTimer = null, longFired = false;
        cell.addEventListener('pointerdown', () => {
          longFired = false;
          pressTimer = setTimeout(() => {
            longFired = true;
            if (confirm(`Uproot the ${sp.name}? Crumbs not refunded.`)) clearPlot(i);
          }, 500);
        });
        const cancelPress = () => { clearTimeout(pressTimer); pressTimer = null; };
        cell.addEventListener('pointerup', () => {
          cancelPress();
          if (!longFired) {
            const minutes = Math.floor(remaining / 60000), seconds = Math.floor((remaining % 60000) / 1000);
            toast(`${sp.name} · ${minutes}:${seconds.toString().padStart(2, '0')} left · long-press to uproot`);
          }
        });
        cell.addEventListener('pointercancel', cancelPress);
        cell.addEventListener('pointerleave', cancelPress);
      }
    }
    gardenGridEl.appendChild(cell);
  }
  if (gardenBonusEl) gardenBonusEl.textContent = `+${(gardenBonus() * 100).toFixed(1)}% permanent rate`;
  if (waterBtnEl) waterBtnEl.disabled = (state.count || 0) < GARDEN_WATER_COST;
  if (gardenSummaryEl) gardenSummaryEl.textContent = `crumbs: ${state.crumbs || 0} · barley harvests cap at +${(GARDEN_BARLEY_CAP * 100).toFixed(0)}% (currently +${(gardenBonus() * 100).toFixed(1)}%)`;
}
